// @ts-nocheck
/**
 * Pools — two views, Tourney Machine style.
 *
 *  View 1 (list):   one collapsible card per pool, Add Pool, Edit name & level, Delete pool.
 *  View 2 (assign): No Pool + one column per pool; every drop saves right away.
 *
 * Every write goes through rle-operator-schedule-manual "pools_apply" with
 * regenerate_games: false and generate_crossovers: false. Saving never creates games.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Loader2, Users, Lock, ChevronDown, ChevronRight, MoreHorizontal, ChevronLeft, GripVertical, Wand2, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { supabase } from '@/integrations/supabase/client';
import { formatCalendarDate, formatTime } from '@/lib/dates';
import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, TouchSensor, pointerWithin, useDraggable, useDroppable,
  useSensor, useSensors,
} from '@dnd-kit/core';
import DivisionAdvancedSettings from './DivisionAdvancedSettings';
import { useIsMobile } from '@/hooks/use-mobile';

const NONE = '__none__';
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const call = async (body: Record<string, unknown>) => {
  const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', { body });
  if (error) {
    let message = error.message || 'Request failed';
    try {
      const ctx = (error as any).context;
      if (ctx && typeof ctx.json === 'function') {
        const parsed = await ctx.json();
        message = parsed?.error?.message || message;
      }
    } catch { /* keep the original message */ }
    return { ok: false, message };
  }
  if (!res?.ok) return { ok: false, message: res?.error?.message ?? 'Request failed' };
  return { ok: true, data: res.data };
};

/** Load one event's pools and save ops for one division. */
function usePools(eventId: string) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    const res = await call({ action: 'pools_data', event_id: eventId });
    setLoading(false);
    if (!res.ok) { toast.error(res.message); return; }
    setData(res.data);
  }, [eventId]);
  useEffect(() => { void load(); }, [load]);

  /** Saving never creates games. */
  const save = useCallback(async (division: string, ops: any[], extra: Record<string, unknown> = {}) => {
    return call({
      action: 'pools_apply',
      event_id: eventId,
      division,
      ops,
      autobalance: false,
      ...extra,
      regenerate_games: false,
      generate_crossovers: false,
    });
  }, [eventId]);

  return { data, setData, loading, load, save, locked: !!data?.published_lock };
}

function nextPoolName(pools: any[]) {
  for (const L of LETTERS) {
    const n = `Pool ${L}`;
    if (!pools.some((p) => p.name === n)) return n;
  }
  return `Pool ${pools.length + 1}`;
}

interface Props {
  eventId: string;
  /** Limit to one division (divisions-first navigation). */
  division?: string;
  hideHeading?: boolean;
  onChanged?: () => void;
  /** Which view to show. When omitted the panel switches views itself. */
  view?: 'list' | 'assign';
  onOpenAssign?: () => void;
  onBack?: () => void;
  /** Opens the round robin screen for one pool. */
  onOpenRoundRobin?: (poolId: string) => void;
  /** Opens the print page for one pool in a new tab. */
  onOpenPrint?: (poolId: string) => void;
}

const PoolsPanel: React.FC<Props> = ({ eventId, division: only, onChanged, view, onOpenAssign, onBack, onOpenRoundRobin, onOpenPrint }) => {
  const pools = usePools(eventId);
  const [picked, setPicked] = useState<string | null>(null);
  const [localView, setLocalView] = useState<'list' | 'assign'>('list');
  const divisions: any[] = pools.data?.divisions ?? [];
  const divName = only ?? picked ?? divisions[0]?.division ?? null;
  const current = divisions.find((d: any) => d.division === divName) ?? null;
  const shown = view ?? localView;
  const openAssign = onOpenAssign ?? (() => setLocalView('assign'));
  const back = onBack ?? (() => setLocalView('list'));

  if (pools.loading && !pools.data) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!current) {
    return (
      <Card>
        <CardContent className="py-10 flex flex-col items-center gap-3 text-center">
          <Users className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground max-w-md">
            No registered teams yet. Pools can be built once teams register.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="w-full space-y-4">
      {!only && divisions.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {divisions.map((d: any) => (
            <Button
              key={d.division}
              size="sm"
              variant={d.division === divName ? 'default' : 'outline'}
              onClick={() => { setPicked(d.division); setLocalView('list'); }}
            >
              {d.division}
            </Button>
          ))}
        </div>
      )}
      {pools.locked && (
        <Badge variant="outline" className="gap-1">
          <Lock className="h-3 w-3" /> Published — editing locked
        </Badge>
      )}
      {shown === 'assign' ? (
        <AssignView eventId={eventId} current={current} pools={pools} onBack={back} onChanged={onChanged} />
      ) : (
        <ListView
          eventId={eventId}
          current={current}
          pools={pools}
          onAssign={openAssign}
          onChanged={onChanged}
          onRoundRobin={onOpenRoundRobin ?? ((poolId: string) => {
            // Fallback for screens that mount PoolsPanel without routing props.
            window.location.assign(`/admin-dashboard/events/${eventId}/divisions/${encodeURIComponent(current?.division ?? '')}/pools/${poolId}/round-robin`);
          })}
          onPrint={onOpenPrint ?? ((poolId: string) => {
            window.open(`/admin-dashboard/events/${eventId}/divisions/${encodeURIComponent(current?.division ?? '')}/pools/${poolId}/print`, '_blank', 'noopener');
          })}
        />
      )}
    </div>
  );
};

/* ── View 1: Pools list ─────────────────────────────────────────── */
function ListView({ eventId, current, pools, onAssign, onChanged, onRoundRobin, onPrint }: any) {
  const { locked, save, load } = pools;
  const list: any[] = current.pools ?? [];
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [edit, setEdit] = useState<any>(null);
  const [editName, setEditName] = useState('');
  const [editLevel, setEditLevel] = useState('');
  const [del, setDel] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [stTok, setStTok] = useState(0);
  const standings = usePoolStandings(eventId, current.division, `${stTok}|${list.map((x: any) => x.id).join(',')}`);
  const [placesPool, setPlacesPool] = useState<any>(null);
  const [courtsPool, setCourtsPool] = useState<any>(null);
  const [poolCourtNames, setPoolCourtNames] = useState<Record<string, string[]>>({});
  const listKey = list.map((x: any) => x.id).join(',');
  const loadPoolCourtNames = useCallback(async () => {
    const out: Record<string, string[]> = {};
    await Promise.all(list.map(async (p: any) => {
      const r = await call({ action: 'pool_courts_data', event_id: eventId, pool_id: p.id });
      if (r.ok && r.data?.selected_court_ids?.length) {
        const names = new Map((r.data.courts ?? []).map((c: any) => [c.id, c.name]));
        out[p.id] = r.data.selected_court_ids.map((id: string) => names.get(id) ?? 'Court');
      }
    }));
    setPoolCourtNames(out);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, listKey]);
  useEffect(() => { void loadPoolCourtNames(); }, [loadPoolCourtNames]);

  const run = async (ops: any[], ok: string) => {
    setBusy(true);
    const res = await save(current.division, ops);
    setBusy(false);
    if (!res.ok) { toast.error(res.message); return false; }
    toast.success(ok);
    await load(true);
    onChanged?.();
    return true;
  };

  const toggle = (id: string) => setOpenIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const submitAdd = async () => {
    const name = addName.trim();
    if (!name) return;
    if (await run([{ type: 'create_pool', name }], `${name} added`)) setAddOpen(false);
  };
  const submitEdit = async () => {
    const name = editName.trim();
    if (!name) return;
    if (await run([{ type: 'rename_pool', pool_id: edit.id, name, level: editLevel.trim() || null }], 'Pool saved')) setEdit(null);
  };
  const submitDelete = async () => {
    const p = del;
    setDel(null);
    // remove_pool with move_to: null — the server decides whether the teams can go back to No Pool.
    await run([{ type: 'remove_pool', pool_id: p.id, move_to: null }], `${p.name} deleted`);
  };

  return (
    <>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-bold">Pools ({list.length})</h2>
          <div className="text-sm text-muted-foreground">{current.division}</div>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={onAssign}>Assign Teams to Pools</Button>
          <Button
            size="sm"
            disabled={locked}
            onClick={() => { setAddName(nextPoolName(list)); setAddOpen(true); }}
          >
            <Plus className="mr-1 h-4 w-4" /> Add Pool
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {list.length === 0 && (
          <Card><CardContent className="py-6 text-sm text-muted-foreground">No pools yet. Press Add Pool to create one.</CardContent></Card>
        )}
        {list.map((p: any) => {
          const isOpen = openIds.has(p.id);
          return (
            <Card key={p.id} className="w-full">
              <div className="flex items-center gap-2 px-4 py-3">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  onClick={() => toggle(p.id)}
                  aria-expanded={isOpen}
                >
                  {isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-semibold break-words">
                      {p.name}{p.level ? <span className="ml-2 text-xs font-normal text-muted-foreground">Level {p.level}</span> : null}
                    </div>
                    <div className="text-xs text-muted-foreground">{p.teams.length} Team{p.teams.length === 1 ? '' : 's'}</div>
                    {poolCourtNames[p.id]?.length ? (
                      <div className="text-xs text-muted-foreground break-words">Courts: {poolCourtNames[p.id].join(', ')}</div>
                    ) : null}
                  </div>
                </button>
                {isOpen && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="icon" variant="ghost" className="h-8 w-8" disabled={locked || busy} aria-label="Pool options">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onSelect={() => { setEdit(p); setEditName(p.name); setEditLevel(p.level ?? ''); }}>
                        Edit name &amp; level
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onSelect={() => setDel(p)}>
                        Delete pool
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              {isOpen && (
                <div className="flex flex-wrap gap-2 border-t px-4 py-2">
                  <Button size="sm" variant="outline" disabled={locked} onClick={() => setCourtsPool(p)}>
                    Select Courts to Schedule
                  </Button>
                  {p.teams.length >= 2 && onRoundRobin && (
                    <Button size="sm" variant="outline" onClick={() => onRoundRobin(p.id)}>
                      Generate Round Robin Matchups
                    </Button>
                  )}
                  {onPrint && (
                    <Button size="sm" variant="outline" onClick={() => onPrint(p.id)}>
                      Print Standings and Results
                    </Button>
                  )}
                </div>
              )}
              {isOpen && (
                <div className="border-t px-4 py-3">
                  {p.teams.length === 0 ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-sm text-muted-foreground">No teams in this pool yet</span>
                      <Button size="sm" variant="outline" onClick={onAssign}>Assign Teams</Button>
                    </div>
                  ) : (
                    <>
                      <h3 className="mb-1 text-sm font-semibold">Pool Places</h3>
                      <PoolStandingsTable teams={p.teams} rows={standings[p.id] ?? []} />
                      <div className="mt-3">
                        <Button size="sm" onClick={() => setPlacesPool(p)}>Set Pool Places</Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <DivisionAdvancedSettings
        eventId={eventId}
        division={current.division}
        onSaved={() => { void load(true); onChanged?.(); }}
      />

      <SelectPoolCourtsPanel
        eventId={eventId}
        pool={courtsPool}
        division={current.division}
        onClose={() => setCourtsPool(null)}
        onSaved={() => { void loadPoolCourtNames(); onChanged?.(); }}
      />

      <SetPoolPlacesPanel
        eventId={eventId}
        pool={placesPool}
        rows={placesPool ? standings[placesPool.id] ?? [] : []}
        onClose={() => setPlacesPool(null)}
        onSaved={() => { setStTok((n) => n + 1); onChanged?.(); }}
      />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-sm">
          <form onSubmit={(e) => { e.preventDefault(); void submitAdd(); }}>
            <DialogHeader><DialogTitle>Add Pool</DialogTitle></DialogHeader>
            <div className="space-y-1.5 py-4">
              <Label htmlFor="pool-name">Pool Name</Label>
              <Input id="pool-name" autoFocus value={addName} onChange={(e) => setAddName(e.target.value)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy || !addName.trim()}>
                {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-sm">
          <form onSubmit={(e) => { e.preventDefault(); void submitEdit(); }}>
            <DialogHeader><DialogTitle>Edit name &amp; level</DialogTitle></DialogHeader>
            <div className="space-y-3 py-4">
              <div className="space-y-1.5">
                <Label htmlFor="edit-pool-name">Name</Label>
                <Input id="edit-pool-name" autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-pool-level">Level</Label>
                <Input id="edit-pool-level" placeholder="None" value={editLevel} onChange={(e) => setEditLevel(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
              <Button type="submit" disabled={busy || !editName.trim()}>
                {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {del?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {del?.name} has {del?.teams?.length ?? 0} team{del?.teams?.length === 1 ? '' : 's'}. They go back to No Pool.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={submitDelete}>
              Delete pool
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* ── View 2: Assign Teams to Pools ──────────────────────────────── */
function TeamCard({ team, columns, from, disabled, isMobile, onMove }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `card:${team.registration_id}`,
    data: { team, from },
    disabled,
  });
  const name = <span className="min-w-0 flex-1 break-words text-sm">{team.name}</span>;
  return (
    <div
      ref={setNodeRef}
      className={`flex items-start gap-2 rounded-md border bg-card px-2 py-2 shadow-sm ${isDragging ? 'opacity-40' : ''}`}
    >
      <button
        type="button"
        className={`touch-none pt-0.5 text-muted-foreground ${disabled ? 'opacity-40' : 'cursor-grab active:cursor-grabbing'}`}
        aria-label={`Drag ${team.name}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      {isMobile && !disabled ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="min-w-0 flex-1 text-left">{name}</button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>Move to</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {columns.filter((c) => c.id !== from).map((c) => (
              <DropdownMenuItem key={c.id} onSelect={() => onMove(team, from, c.id)}>{c.name}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : name}
    </div>
  );
}

function Column({ col, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${col.id}` });
  return (
    <div
      ref={setNodeRef}
      className={`flex min-h-[160px] w-full flex-col rounded-lg border-2 p-2 transition-colors md:w-auto md:min-w-[240px] md:flex-1 md:basis-[240px] ${
        isOver ? 'border-primary bg-primary/5' : 'border-dashed border-border bg-muted/30'
      }`}
    >
      <div className="mb-2 flex items-baseline justify-between gap-2 px-1">
        <span className="font-semibold break-words">{col.name}</span>
        <span className="text-xs text-muted-foreground">{col.teams.length} team{col.teams.length === 1 ? '' : 's'}</span>
      </div>
      <div className="flex flex-1 flex-col gap-1.5">
        {col.teams.length === 0 && (
          <div className="flex flex-1 items-center justify-center py-6 text-xs text-muted-foreground">Drop teams here</div>
        )}
        {children}
      </div>
    </div>
  );
}

function AssignView({ eventId, current, pools, onBack, onChanged }) {
  const { locked, save, load, setData } = pools;
  const isMobile = useIsMobile();
  const [dragTeam, setDragTeam] = useState<any>(null);
  const [fillOpen, setFillOpen] = useState(false);
  const [filling, setFilling] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const columns = useMemo(() => [
    { id: NONE, name: 'No Pool', teams: current.unplaced_teams ?? [] },
    ...(current.pools ?? []).map((p: any) => ({ id: p.id, name: p.name, teams: p.teams ?? [] })),
  ], [current]);
  const totalTeams = columns.reduce((n, c) => n + c.teams.length, 0);

  /** Move a team in the local copy so the card lands at once. */
  const patch = (team, from, to) => {
    setData((d: any) => ({
      ...d,
      divisions: d.divisions.map((x: any) => {
        if (x.division !== current.division) return x;
        const strip = (arr: any[]) => arr.filter((t) => t.registration_id !== team.registration_id);
        const unplaced = from === NONE ? strip(x.unplaced_teams ?? []) : (x.unplaced_teams ?? []);
        return {
          ...x,
          unplaced_teams: to === NONE ? [...unplaced, team] : unplaced,
          pools: x.pools.map((p: any) => {
            let teams = p.id === from ? strip(p.teams) : p.teams;
            if (p.id === to) teams = [...teams, team];
            return { ...p, teams };
          }),
        };
      }),
    }));
  };

  const move = async (team, from, to) => {
    if (locked || from === to) return;
    patch(team, from, to);
    const res = await save(current.division, [
      { type: 'move_team', registration_id: team.registration_id, to_pool_id: to === NONE ? null : to },
    ]);
    if (!res.ok) {
      patch(team, to, from);
      toast.error(res.message);
      return;
    }
    toast.success('Saved');
    await load(true);
    onChanged?.();
  };

  const onDragEnd = (e) => {
    setDragTeam(null);
    const over = e.over ? String(e.over.id) : '';
    if (!over.startsWith('col:')) return;
    const { team, from } = e.active.data.current ?? {};
    if (team) void move(team, from, over.slice(4));
  };

  /** Auto-fill: existing pools_plan autobalance over the current pools, then pools_apply. */
  const autoFill = async () => {
    setFillOpen(false);
    const poolList = current.pools ?? [];
    if (poolList.length === 0) return;
    const n = poolList.length;
    const sizes = Array.from({ length: n }, (_, i) => Math.floor(totalTeams / n) + (i < totalTeams % n ? 1 : 0)).filter((s) => s > 0);
    const body = {
      ops: (current.unplaced_teams ?? []).map((t: any) => ({
        type: 'move_team', registration_id: t.registration_id, to_pool_id: poolList[0].id,
      })),
      autobalance: true,
      split_sizes: sizes,
    };
    setFilling(true);
    // Same plan as pools_plan would preview; check it before saving.
    const plan = await call({ action: 'pools_plan', event_id: eventId, division: current.division, ...body });
    if (!plan.ok) { setFilling(false); toast.error(plan.message); return; }
    const res = await save(current.division, body.ops, { autobalance: true, split_sizes: sizes });
    setFilling(false);
    if (!res.ok) { toast.error(res.message); return; }
    toast.success('Pools filled');
    await load(true);
    onChanged?.();
  };

  const noPools = (current.pools ?? []).length === 0;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onBack}>
          <ChevronLeft className="mr-1 h-4 w-4" /> Go Back
        </Button>
        <div className="ml-auto flex items-center gap-2">
          {noPools && <span className="text-xs text-muted-foreground">Add pools first</span>}
          <Button size="sm" disabled={locked || noPools || filling} onClick={() => setFillOpen(true)}>
            {filling ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Wand2 className="mr-1 h-4 w-4" />}
            Auto-fill pools
          </Button>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-bold">Assign Teams to Pools</h2>
        <div className="text-sm text-muted-foreground">{current.division}</div>
        <p className="mt-1 text-sm text-muted-foreground">Drag teams into pools. Every move saves right away.</p>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={(e) => setDragTeam(e.active.data.current?.team ?? null)}
        onDragCancel={() => setDragTeam(null)}
        onDragEnd={onDragEnd}
      >
        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap">
          {columns.map((col) => (
            <Column key={col.id} col={col}>
              {col.teams.map((t: any) => (
                <TeamCard
                  key={t.registration_id}
                  team={t}
                  from={col.id}
                  columns={columns}
                  disabled={locked}
                  isMobile={isMobile}
                  onMove={move}
                />
              ))}
            </Column>
          ))}
        </div>
        <DragOverlay>
          {dragTeam && (
            <div className="flex max-w-[280px] items-start gap-2 rounded-md border bg-card px-2 py-2 shadow-lg">
              <GripVertical className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <span className="break-words text-sm">{dragTeam.name}</span>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      <AlertDialog open={fillOpen} onOpenChange={setFillOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Auto-fill pools</AlertDialogTitle>
            <AlertDialogDescription>
              Place all {totalTeams} teams into the existing pools by seed (snake order)? Teams already placed will be moved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={autoFill}>Auto-fill</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default PoolsPanel;

/* ── Generate Round Robin Matchups (one pool) ───────────────────── */
/** Like `call`, but keeps the full response body and status on errors. */
const rrCall = async (body: Record<string, unknown>) => {
  const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', { body });
  if (error) {
    let parsed: any = null;
    let status = 0;
    try {
      const ctx = (error as any).context;
      status = ctx?.status ?? 0;
      if (ctx && typeof ctx.json === 'function') {
        parsed = await (typeof ctx.clone === 'function' ? ctx.clone() : ctx).json();
      }
    } catch { /* keep the original message */ }
    return { ok: false, status, data: parsed?.data, message: parsed?.error?.message || error.message || 'Request failed' };
  }
  if (!res?.ok) return { ok: false, status: 200, data: res?.data, message: res?.error?.message ?? 'Request failed' };
  return { ok: true, status: 200, data: res.data };
};

export function PoolRoundRobin({ eventId, poolId, onBack, onDone }: {
  eventId: string; poolId: string; onBack: () => void; onDone: () => void;
}) {
  const [info, setInfo] = useState<any>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [picks, setPicks] = useState<string[]>([]); // game k = picks[2k] vs picks[2k+1]
  const [gpt, setGpt] = useState(1);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<number | null>(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      const res = await rrCall({ action: 'pool_round_robin_data', event_id: eventId, pool_id: poolId });
      if (dead) return;
      if (!res.ok) { setLoadErr(res.message); return; }
      setInfo(res.data);
      const pairs = Math.floor((res.data.teams?.length ?? 0) / 2);
      setPicks(new Array(pairs * 2).fill(''));
      setGpt(Math.max(1, res.data.max_games_per_team ?? 1));
    })();
    return () => { dead = true; };
  }, [eventId, poolId]);

  if (loadErr) {
    return (
      <div className="w-full space-y-4">
        <Button size="sm" variant="ghost" onClick={onBack}><ChevronLeft className="mr-1 h-4 w-4" /> Go Back</Button>
        <Card><CardContent className="py-6 text-sm text-destructive">{loadErr}</CardContent></Card>
      </div>
    );
  }
  if (!info) {
    return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  const teams: any[] = info.teams ?? [];
  const pairs = Math.floor(teams.length / 2);
  const maxG = info.max_games_per_team ?? 1;
  const chosen = new Set(picks.filter(Boolean));
  const leftover = teams.length % 2 === 1 && chosen.size === pairs * 2
    ? teams.find((t) => !chosen.has(t.registration_id)) : null;
  const allFilled = picks.length > 0 && picks.every(Boolean);

  const setPick = (i: number, v: string) => setPicks((p) => { const n = [...p]; n[i] = v; return n; });

  // Circle method, same slot layout as the server: game k = slot k vs slot m-1-k.
  const rotate = () => {
    const m = teams.length % 2 === 0 ? teams.length : teams.length + 1;
    const slots: string[] = new Array(m).fill('');
    for (let k = 0; k < pairs; k++) { slots[k] = picks[2 * k]; slots[m - 1 - k] = picks[2 * k + 1]; }
    if (m > teams.length) {
      // Odd count: the team sitting out fills the middle slot, the bye the other.
      slots[pairs] = teams.find((t) => !picks.includes(t.registration_id))?.registration_id ?? '';
    }
    const r = [slots[0], slots[m - 1], ...slots.slice(1, m - 1)];
    const next: string[] = [];
    for (let k = 0; k < pairs; k++) next.push(r[k] ?? '', r[m - 1 - k] ?? '');
    setPicks(next);
  };

  const generate = async (replace = false) => {
    setBusy(true);
    const round1: string[][] = [];
    for (let k = 0; k < pairs; k++) round1.push([picks[2 * k], picks[2 * k + 1]]);
    const res = await rrCall({
      action: 'pool_round_robin', event_id: eventId, pool_id: poolId,
      games_per_team: gpt, round1, ...(replace ? { replace: true } : {}),
    });
    setBusy(false);
    if (!res.ok) {
      if (res.status === 409 && res.data?.needs_replace) { setConfirm(res.data.existing_games ?? 0); return; }
      toast.error(res.message);
      return;
    }
    const d = res.data;
    const extra = [...(d.short_teams ?? []), ...(d.prevented_skipped ?? []).map((s: string) => `Skipped: ${s}`)];
    toast.success(`${d.games_created} games created for ${info.pool.name}`, extra.length ? { description: extra.join('\n') } : undefined);
    onDone();
  };

  const selectCls = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm';

  return (
    <div className="w-full space-y-4">
      <Button size="sm" variant="ghost" onClick={onBack}><ChevronLeft className="mr-1 h-4 w-4" /> Go Back</Button>
      <div>
        <h2 className="text-xl font-bold">Generate Round Robin Matchups</h2>
        <div className="text-sm text-muted-foreground">{info.pool.name}</div>
      </div>
      {info.existing_games > 0 && (
        <div className="rounded-md border px-3 py-2 text-sm">
          {info.pool.name} already has {info.existing_games} games. Generating replaces the unplayed ones.
        </div>
      )}
      <p className="text-sm">Pick the round 1 matchups and how many games each team plays. The other rounds rotate from round 1.</p>

      <Card>
        <CardContent className="space-y-3 py-4">
          <div className="font-semibold">Round 1</div>
          {Array.from({ length: pairs }).map((_, k) => (
            <div key={k} className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {[0, 1].map((side) => {
                const i = 2 * k + side;
                return (
                  <React.Fragment key={side}>
                    {side === 1 && <span className="text-center text-sm text-muted-foreground sm:px-2">vs</span>}
                    <select
                      className={selectCls}
                      value={picks[i] ?? ''}
                      onChange={(e) => setPick(i, e.target.value)}
                      aria-label={`Round 1 game ${k + 1} team ${side === 0 ? 'A' : 'B'}`}
                    >
                      <option value="">Select team</option>
                      {teams.map((t) => (
                        <option
                          key={t.registration_id}
                          value={t.registration_id}
                          disabled={chosen.has(t.registration_id) && picks[i] !== t.registration_id}
                        >
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </React.Fragment>
                );
              })}
            </div>
          ))}
          {leftover && <div className="text-sm text-muted-foreground">{leftover.name} sits out round 1</div>}
          <Button size="sm" variant="outline" onClick={rotate} disabled={!allFilled}>Rotate teams one position</Button>
        </CardContent>
      </Card>

      <div className="space-y-1">
        <Label htmlFor="rr-gpt">Number of games for each team</Label>
        <Input
          id="rr-gpt"
          type="number"
          min={1}
          max={maxG}
          value={gpt}
          onChange={(e) => setGpt(Math.max(1, Math.min(maxG, Number(e.target.value) || 1)))}
          className="w-32"
        />
      </div>

      <Button onClick={() => generate(false)} disabled={!allFilled || busy}>
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Generate matchups
      </Button>

      <AlertDialog open={confirm !== null} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace games?</AlertDialogTitle>
            <AlertDialogDescription>
              {info.pool.name} already has {confirm} games. Replace them? Games with a score are never replaced.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirm(null); void generate(true); }}>Replace</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ── Pool standings (Tourney Machine layout) ────────────────────── */
/** Loads standings_data once for a division and groups rows by pool_id. */
export function usePoolStandings(eventId: string, division: string | null, token: unknown = 0) {
  const [byPool, setByPool] = useState<Record<string, any[]>>({});
  useEffect(() => {
    if (!division) return;
    let dead = false;
    (async () => {
      const res = await call({ action: 'standings_data', event_id: eventId, division });
      if (dead || !res.ok) return;
      const g: Record<string, any[]> = {};
      for (const r of res.data?.rows ?? []) (g[r.pool_id] ??= []).push(r);
      for (const k of Object.keys(g)) {
        g[k].sort((a, b) => (a.games_played || a.pool_place_manual ? a.pool_rank ?? 999 : 999) - (b.games_played || b.pool_place_manual ? b.pool_rank ?? 999 : 999)
          || String(a.team_name).localeCompare(String(b.team_name)));
      }
      setByPool(g);
    })();
    return () => { dead = true; };
  }, [eventId, division, token]);
  return byPool;
}

/** Every team in the pool, 0s for teams with no standings row yet. */
export function PoolStandingsTable({ teams, rows }: { teams: any[]; rows: any[] }) {
  const byReg = new Map((rows ?? []).map((r: any) => [r.registration_id, r]));
  const list = [
    ...(rows ?? []).filter((r: any) => teams.some((t: any) => t.registration_id === r.registration_id)),
    ...teams.filter((t: any) => !byReg.has(t.registration_id))
      .map((t: any) => ({ registration_id: t.registration_id, team_name: t.name, games_played: 0 })),
  ];
  const n = (v: any) => (v == null ? 0 : v);
  const place = (r: any) => ((!r.games_played && !r.pool_place_manual) || r.pool_rank == null ? '-' : `${r.pool_rank_tied ? 'T-' : ''}${r.pool_rank}`);
  const th = 'px-2 py-1.5 text-right font-semibold whitespace-nowrap';
  const td = 'px-2 py-1.5 text-right tabular-nums whitespace-nowrap';
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full min-w-[560px] border-collapse text-sm">
        <thead>
          <tr className="border-b">
            <th className="sticky left-0 z-10 bg-card px-2 py-1.5 text-left font-semibold">Team</th>
            <th className={th}>Place</th><th className={th}>W</th><th className={th}>L</th><th className={th}>T</th>
            <th className={th}>PS</th><th className={th}>PA</th><th className={th}>PD</th><th className={th}>PDM</th><th className={th}>HH PD</th>
          </tr>
        </thead>
        <tbody>
          {list.map((r: any) => (
            <tr key={r.registration_id} className="border-b last:border-0">
              <td className="sticky left-0 z-10 bg-card px-2 py-1.5 text-left break-words min-w-[140px] max-w-[240px]">{r.team_name}</td>
              <td className={td}>{place(r)}{r.pool_place_manual ? <span className="ml-0.5 text-muted-foreground" title="Set by hand">✎</span> : null}</td>
              <td className={td}>{n(r.wins)}</td><td className={td}>{n(r.losses)}</td><td className={td}>{n(r.ties)}</td>
              <td className={td}>{n(r.points_for)}</td><td className={td}>{n(r.points_against)}</td>
              <td className={td}>{n(r.raw_diff)}</td><td className={td}>{n(r.diff)}</td><td className={td}>{n(r.hh_pd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Print Standings and Results (one pool) ─────────────────────── */
export function PoolPrintPage({ eventId, division, poolId }: { eventId: string; division: string; poolId: string }) {
  const standings = usePoolStandings(eventId, division);
  const [pool, setPool] = useState<any>(null);
  const [games, setGames] = useState<any[]>([]);
  const [courts, setCourts] = useState<Record<string, string>>({});
  const [scores, setScores] = useState<Record<string, any>>({});
  const [eventName, setEventName] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      const [pd, bd, ev] = await Promise.all([
        call({ action: 'pools_data', event_id: eventId }),
        call({ action: 'bulk_edit_data', event_id: eventId }),
        (supabase as any).from('events').select('name').eq('id', eventId).maybeSingle(),
      ]);
      if (dead) return;
      if (!pd.ok) { setErr(pd.message); setLoading(false); return; }
      const d = (pd.data?.divisions ?? []).find((x: any) => x.division === division);
      setPool((d?.pools ?? []).find((p: any) => p.id === poolId) ?? null);
      setEventName(ev?.data?.name ?? '');
      const gs = bd.ok ? (bd.data?.games ?? []).filter((g: any) => g.pool_id === poolId) : [];
      gs.sort((a: any, b: any) => (a.round_number ?? 0) - (b.round_number ?? 0) || (a.game_number ?? 0) - (b.game_number ?? 0));
      setGames(gs);
      setCourts(Object.fromEntries((bd.data?.courts ?? []).map((c: any) => [c.id, c.name])));
      if (gs.length) {
        const { data: sc } = await (supabase as any).from('scheduled_games')
          .select('id, score_a, score_b').in('id', gs.map((g: any) => g.id));
        if (!dead) setScores(Object.fromEntries((sc ?? []).map((s: any) => [s.id, s])));
      }
      if (!dead) setLoading(false);
    })();
    return () => { dead = true; };
  }, [eventId, division, poolId]);

  const dash = (v: any) => (v == null || v === '' ? '-' : v);
  const when = (g: any) => {
    const dt = [formatCalendarDate(g.scheduled_date, 'EEE MMM d'), formatTime(g.scheduled_start_time)].filter(Boolean).join(' ');
    return dt || '-';
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (err) return <div className="p-6 text-sm text-destructive">{err}</div>;

  return (
    <div className="mx-auto max-w-[7.5in] bg-background p-6 text-foreground print:p-0">
      <style>{'@page { size: letter; margin: 0.5in; } @media print { body { background: white; } }'}</style>
      <div className="mb-4 flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold break-words">{eventName || 'Event'}</h1>
          <div className="text-sm">{division} — {pool?.name ?? 'Pool'}</div>
        </div>
        <Button size="sm" className="print:hidden" onClick={() => window.print()}>Print</Button>
      </div>
      <h2 className="mb-1 text-base font-semibold">Pool Places</h2>
      <PoolStandingsTable teams={pool?.teams ?? []} rows={standings[poolId] ?? []} />
      <h2 className="mb-1 mt-5 text-base font-semibold">Results</h2>
      {games.length === 0 ? (
        <div className="text-sm text-muted-foreground">No games in this pool yet.</div>
      ) : (
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b text-left">
              <th className="px-1 py-1">Round</th><th className="px-1 py-1">Game</th>
              <th className="px-1 py-1">Team A</th><th className="px-1 py-1 text-right">Score</th>
              <th className="px-1 py-1">Team B</th><th className="px-1 py-1 text-right">Score</th>
              <th className="px-1 py-1">Court</th><th className="px-1 py-1">Date/Time</th>
            </tr>
          </thead>
          <tbody>
            {games.map((g: any) => (
              <tr key={g.id} className="border-b last:border-0 align-top">
                <td className="px-1 py-1">{dash(g.round_number)}</td>
                <td className="px-1 py-1">{dash(g.game_number)}</td>
                <td className="px-1 py-1 break-words">{dash(g.team_a_name)}</td>
                <td className="px-1 py-1 text-right tabular-nums">{dash(scores[g.id]?.score_a)}</td>
                <td className="px-1 py-1 break-words">{dash(g.team_b_name)}</td>
                <td className="px-1 py-1 text-right tabular-nums">{dash(scores[g.id]?.score_b)}</td>
                <td className="px-1 py-1">{dash(g.court_id ? courts[g.court_id] : null)}</td>
                <td className="px-1 py-1 whitespace-nowrap">{when(g)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ── Set Pool Places (one pool, right-side panel) ───────────────── */
function SetPoolPlacesPanel({ eventId, pool, rows, onClose, onSaved }: {
  eventId: string; pool: any; rows: any[]; onClose: () => void; onSaved: () => void;
}) {
  const [vals, setVals] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const teams: any[] = pool?.teams ?? [];
  const byReg = new Map((rows ?? []).map((r: any) => [r.registration_id, r]));
  const list = teams.map((t: any) => byReg.get(t.registration_id) ?? { registration_id: t.registration_id, team_name: t.name });
  const shownPlace = (r: any) => (r.pool_rank == null || (!r.games_played && !r.pool_place_manual) ? '' : String(r.pool_rank));

  useEffect(() => {
    setVals(Object.fromEntries(list.map((r: any) => [r.registration_id, shownPlace(r)])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool?.id, rows]);

  const ties = new Map<number, string[]>();
  for (const r of list) if (r.pool_rank_tied && r.pool_rank != null) {
    if (!ties.has(r.pool_rank)) ties.set(r.pool_rank, []);
    ties.get(r.pool_rank)!.push(r.team_name);
  }

  const commit = async (r: any) => {
    const v = (vals[r.registration_id] ?? '').trim();
    if (v === shownPlace(r) && !(v === '' && r.pool_place_manual)) return;
    if (v === '' && !r.pool_place_manual) return;
    setBusy(true);
    const res = await call({ action: 'pool_places_set', event_id: eventId, pool_id: pool.id, registration_id: r.registration_id, place: v === '' ? null : Number(v) });
    setBusy(false);
    if (!res.ok) { toast.error(res.message); setVals((s) => ({ ...s, [r.registration_id]: shownPlace(r) })); return; }
    onSaved();
  };

  const clearAll = async () => {
    setConfirmClear(false);
    setBusy(true);
    const res = await call({ action: 'pool_places_clear', event_id: eventId, pool_id: pool.id });
    setBusy(false);
    if (!res.ok) { toast.error(res.message); return; }
    toast.success('Calculated places restored');
    onSaved();
  };

  return (
    <>
      <Sheet open={!!pool} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent side="right" className="w-full max-w-full overflow-y-auto sm:max-w-[480px]">
          <SheetHeader>
            <SheetTitle>Set Pool Places</SheetTitle>
            <SheetDescription>{pool?.name}</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            {[...ties.entries()].map(([n, names]) => (
              <div key={n} className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
                {names.join(' and ')} are tied for place {n}. Set their places by hand.
              </div>
            ))}
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="px-1 py-1.5">Team</th><th className="px-1 py-1.5">Place</th>
                  <th className="px-1 py-1.5 text-right">W</th><th className="px-1 py-1.5 text-right">L</th><th className="px-1 py-1.5 text-right">PD</th>
                </tr>
              </thead>
              <tbody>
                {list.map((r: any) => (
                  <tr key={r.registration_id} className="border-b last:border-0 align-middle">
                    <td className="px-1 py-1.5 break-words">
                      {r.team_name}
                      {r.pool_place_manual && <Badge variant="outline" className="ml-1 px-1 py-0 text-[10px]">Set by hand</Badge>}
                    </td>
                    <td className="px-1 py-1.5">
                      <Input
                        className="h-8 w-16"
                        inputMode="numeric"
                        disabled={busy}
                        value={vals[r.registration_id] ?? ''}
                        onChange={(e) => setVals((s) => ({ ...s, [r.registration_id]: e.target.value }))}
                        onBlur={() => void commit(r)}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        aria-label={`Place for ${r.team_name}`}
                      />
                    </td>
                    <td className="px-1 py-1.5 text-right tabular-nums">{r.wins ?? 0}</td>
                    <td className="px-1 py-1.5 text-right tabular-nums">{r.losses ?? 0}</td>
                    <td className="px-1 py-1.5 text-right tabular-nums">{r.raw_diff ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirmClear(true)}>Use calculated places</Button>
              <Link className="text-sm underline" to={`/admin-dashboard/events/${eventId}/edit?step=standings`}>Edit tie breakers</Link>
              <Button size="sm" className="ml-auto" onClick={onClose}>Done</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Use calculated places</AlertDialogTitle>
            <AlertDialogDescription>Clear every place set by hand in {pool?.name}?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void clearAll()}>Clear</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* ── Select Courts to Schedule (one pool, right-side panel) ─────── */
function SelectPoolCourtsPanel({ eventId, pool, division, onClose, onSaved }: {
  eventId: string; pool: any; division: string; onClose: () => void; onSaved: () => void;
}) {
  const [courts, setCourts] = useState<any[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pool) return;
    let dead = false;
    setLoading(true);
    (async () => {
      const r = await call({ action: 'pool_courts_data', event_id: eventId, pool_id: pool.id });
      if (dead) return;
      setLoading(false);
      if (!r.ok) { toast.error(r.message); return; }
      setCourts(r.data?.courts ?? []);
      setPicked(new Set(r.data?.selected_court_ids ?? []));
    })();
    return () => { dead = true; };
  }, [eventId, pool?.id]);

  const toggle = (id: string, on: boolean) => setPicked((s) => {
    const n = new Set(s);
    if (on) n.add(id); else n.delete(id);
    return n;
  });

  const save = async () => {
    setBusy(true);
    const ids = courts.map((c: any) => c.id).filter((id: string) => picked.has(id));
    const r = await call({ action: 'pool_courts_set', event_id: eventId, pool_id: pool.id, court_ids: ids });
    setBusy(false);
    if (!r.ok) { toast.error(r.message); return; }
    const names = new Map((r.data?.courts ?? courts).map((c: any) => [c.id, c.name]));
    const sel: string[] = r.data?.selected_court_ids ?? [];
    toast.success(sel.length
      ? `${pool.name} will be scheduled on ${sel.map((id) => names.get(id) ?? 'Court').join(', ')}`
      : `${pool.name} uses all ${division} courts`);
    onSaved();
    onClose();
  };

  return (
    <Sheet open={!!pool} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full max-w-full overflow-y-auto sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>Select Courts to Schedule</SheetTitle>
          <SheetDescription>{pool?.name}</SheetDescription>
        </SheetHeader>
        <p className="mt-3 text-sm text-muted-foreground">
          Pick the courts this pool's games can be scheduled on. Pick none and the pool uses all of {division}'s courts.
        </p>
        <div className="mt-4 space-y-2">
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : courts.length === 0 ? (
            <div className="text-sm text-muted-foreground">No courts available for {division}.</div>
          ) : courts.map((c: any) => (
            <label key={c.id} className="flex items-center gap-2 text-sm">
              <Checkbox checked={picked.has(c.id)} onCheckedChange={(v) => toggle(c.id, v === true)} />
              <span className="break-words">{c.name}</span>
            </label>
          ))}
        </div>
        <SheetFooter className="mt-6 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || loading} onClick={() => void save()}>
            {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Save
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
